import React, { useState, useEffect, lazy, Suspense } from 'react';
import { MDBContainer, MDBBtn, MDBTypography, MDBCol } from 'mdb-react-ui-kit';

import 'react-toastify/dist/ReactToastify.css';
import { useRestaurantContext } from '../../context/restaurants';
import RestaurantsHeader from '../../components/Restaurants/RestaurantsHeader';
import RestaurantsCard from '../../components/Restaurants/RestaurantsCard';
import DownloadInfo from '../../components/DownloadInfo';
import { useDataContext } from '../../context/data';
import translations from '../../constants/translations.json';
import './styles.css';

// Lazy load map to avoid loading Leaflet on list view
const RestaurantsMap = lazy(() => import('../../components/Restaurants/RestaurantsMap'));

export default function Restaurants() {
  const restaurantContext = useRestaurantContext();
  const { language } = useDataContext();

  const [filteredRestaurants, setFilteredRestaurants] = useState([]);
  const [query, setQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'map'

  const categoryOptions = [
    { value: 'distance', label: translations[language]?.restaurants_sort_distance },
    { value: 'name', label: translations[language]?.restaurants_sort_name },
  ];

  useEffect(() => {
    initializeRestaurants();
  }, []);

  useEffect(() => {
    if (restaurantContext.restaurants.length > 0) {
      setFilteredRestaurants([...restaurantContext.restaurants]);
    }
  }, [restaurantContext.restaurants]);

  useEffect(() => {
    filterResto();
  }, [query, selectedCategory]);

  useEffect(() => {
    if (selectedCategory) {
      const categoryFind = categoryOptions.find(
        (option) => option.value === selectedCategory.value,
      );

      if (categoryFind) {
        setSelectedCategory({ ...selectedCategory, label: categoryFind.label });
      }
    }
  }, [language]);

  const initializeRestaurants = async () => {
    localStorage.removeItem('orders');
    localStorage.removeItem('return_url');

    const locCache = window.localStorage.getItem('position');
    if (locCache) {
      setSelectedCategory({
        value: 'distance',
        label: translations[language]?.restaurants_sort_distance,
      });
    }

    await restaurantContext.loadRestaurants();
  };

  const filterResto = () => {
    let filtered = [...restaurantContext.restaurants];

    if (query) {
      filtered = filtered.filter((item) => item.name.toLowerCase().includes(query.toLowerCase()));
    }

    if (selectedCategory) {
      if (selectedCategory.value === 'distance') {
        filtered = filtered.sort((a, b) => {
          if (a.distance == null || b.distance == null) {
            return 0;
          }
          const distanceA = parseFloat(a?.distance?.replace('km', '') || 0);
          const distanceB = parseFloat(b?.distance?.replace('km', '') || 0);

          return distanceA - distanceB;
        });
      }

      if (selectedCategory.value === 'name') {
        filtered = filtered.sort((a, b) => (a.name > b.name ? 1 : b.name > a.name ? -1 : 0));
      }
    }

    setFilteredRestaurants(filtered);
  };

  const handleCategorySelect = (value) => {
    setSelectedCategory(value);
  };

  return (
    <>
      <div className="restaurants-container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mt-4">
        <RestaurantsHeader
          query={query}
          setQuery={setQuery}
          selectedCategory={selectedCategory}
          onCategorySelect={handleCategorySelect}
          categoryOptions={categoryOptions}
          viewMode={viewMode}
          setViewMode={setViewMode}
          filteredCount={filteredRestaurants.length}
        />

        <div className="mt-5">
          {viewMode === 'map' ? (
            <div>
              <Suspense
                fallback={
                  <div className="h-[70vh] flex items-center justify-center bg-gray-50 rounded-xl">
                    <div className="text-center text-gray-400">
                      <i className="fas fa-spinner fa-spin text-3xl mb-2 block" />
                      Loading map...
                    </div>
                  </div>
                }
              >
                <RestaurantsMap restaurants={filteredRestaurants} />
              </Suspense>
            </div>
          ) : (
            <>
              <div className="mt-4 grid justify-items-center md:justify-items-stretch grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10 lg:gap-12">
                {filteredRestaurants.map((restaurant) => (
                  <RestaurantsCard key={restaurant.id} restaurant={restaurant} isFavorite={false} />
                ))}
              </div>

              {filteredRestaurants.length === 0 && query && !restaurantContext.fetching ? (
                <p className="text-center">{translations[language]?.restaurants_no_restaurants}</p>
              ) : null}
            </>
          )}
        </div>
      </div>

      <DownloadInfo />
    </>
  );
}
